import {
  ReferralSource,
  AvailablePreferredDoctors,
  BaseResponse,
  Patient,
  PatientNote,
  PatientInsurancePlan,
  PreferredDoctor,
  PatientNotePCPHistory,
  createApi,
  CreateApiParams,
} from '../api';

declare const healConfig: any;

function createPatientsApi(params?: Partial<CreateApiParams>) {
  return createApi({ ...params, baseURL: healConfig.apiBaseUrl + '/admin' });
}

function createFileManagerApi(params?: Partial<CreateApiParams>) {
  return createApi({ ...params, baseURL: healConfig.fileManagerApiBaseUrl });
}

export function fetchPatient(patientId: string): Promise<Patient> {
  return createPatientsApi()
    .then((api) => api.get<{ data: Patient; description?: string }>(`/patient/${patientId}`))
    .then((r) => {
      if (r.ok && r.data) {
        return r.data.data;
      }
      return Promise.reject(new Error(r.data?.description));
    });
}

export function updateSecondaryContact(patientId: string, data: { firstName: string; lastName: string; phone: string; relationshipId: string }): Promise<Patient> {
  return createPatientsApi()
    .then((api) => api.post<{ data: Patient; description?: string }>(`/patient/${patientId}/secondaryContact`, data))
    .then((r) => {
      if (r.ok && r.data) {
        return r.data.data;
      }
      return Promise.reject(new Error(r.data?.description));
    });
}

export function fetchPreferredDoctors(patientId: string): Promise<PreferredDoctor[]> {
  return createPatientsApi()
    .then((api) => api.get<AvailablePreferredDoctors>(`/patient/${patientId}/preferred-doctors-available`))
    .then((r) => {
      if (r.ok) {
        return r.data?.availableProviders || [];
      } else {
        return Promise.reject(new Error((r.data as any).description));
      }
    });
}

export function createPatientNote(patientId: string, note: string, visitCode?: string, operationsOnly?: boolean): Promise<PatientNote> {
  return createPatientsApi()
    .then((api) => api.post<PatientNote>(`/patient/${patientId}/note`, { note, visitCode, operationsOnly }))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error((r.data as any).description))));
}

export function deletePatientNote(patientId: string, noteId: string): Promise<boolean> {
  return createPatientsApi()
    .then((api) => api.delete(`/patient/${patientId}/note/${noteId}`))
    .then((r) => r.ok);
}

export function updatePatientNote(patientId: string, noteId: string, note: string, visitCode?: string, operationsOnly?: boolean): Promise<PatientNote> {
  return createPatientsApi()
    .then((api) =>
      api.patch<PatientNote>(`/patient/${patientId}/note/${noteId}`, {
        note,
        visitCode,
        operationsOnly,
      })
    )
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error((r.data as any).description))));
}

export function fetchPatientNotes(patientId: string): Promise<PatientNote[]> {
  return createPatientsApi()
    .then((api) => api.get<{ results: PatientNote[]; total: number }>(`/patient/${patientId}/note`))
    .then((r) => (r.ok ? r.data?.results || [] : Promise.reject(new Error((r.data as any).description))));
}

export function fetchAttributionHistory(patientId: string): Promise<{ note: PatientNote; contents: PatientNotePCPHistory }[]> {
  return createPatientsApi()
    .then((api) => api.get<{ results: PatientNote[]; total: number }>(`/patient/${patientId}/pcpNote`))
    .then((r) => {
      if (r.ok && r.data) {
        const history: { note: PatientNote; contents: PatientNotePCPHistory }[] = [];
        r.data.results.forEach((note) => {
          const data: PatientNotePCPHistory = JSON.parse(note.note) as PatientNotePCPHistory;
          history.push({ note: note, contents: data });
        });
        return history;
      } else {
        return Promise.reject(new Error((r.data as any).description));
      }
    });
}

export function fetchPatientInsurances(patientId: string) {
  return createPatientsApi()
    .then((api) => api.get<BaseResponse<PatientInsurancePlan[]>>(`/v1/patients/${patientId}/insurances`))
    .then((response) => {
      const insurances = response.data?.data;
      if (response.ok && insurances) {
        return insurances;
      }

      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function createPatientInsurance(patientId: string, payerId: string, memberId: string, groupId?: string, athenaPackageId?: string): Promise<PatientInsurancePlan> {
  return createPatientsApi({ hideMessage: true })
    .then((api) => api.post<BaseResponse<PatientInsurancePlan>>(`/v1/patients/${patientId}/insurances`, { payerId, memberId, groupId, athenaPackageId }))
    .then((r) => {
      if (r.ok && r.data) {
        return r.data.data;
      }
      return Promise.reject(new Error(r.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function updatePatientInsurancesCoverageOrder(patientId: string, patientInsuranceIds: string[]): Promise<boolean> {
  return createPatientsApi()
    .then((api) => api.put<BaseResponse<boolean>>(`/v1/patients/${patientId}/insurances/coverageOrder`, { patientInsuranceIds }))
    .then((response) => {
      if (response.ok) {
        return true;
      }
      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function deletePatientInsurance(patientId: string, insurancePlanId: string): Promise<boolean> {
  return createPatientsApi()
    .then((api) => api.delete<BaseResponse<boolean>>(`/v1/patients/${patientId}/insurances/${insurancePlanId}`))
    .then((response) => {
      if (response.ok) {
        return true;
      }
      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function fetchReferralSource(patientId: string) {
  return createPatientsApi({ hideMessage: true })
    .then((api) => api.get<BaseResponse<ReferralSource>>(`/patient/${patientId}/referral-source`))
    .then((response) => {
      const referralSource = response.data?.data;
      if (response.ok) {
        return referralSource ?? undefined;
      }

      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function createReferralSource(patientId: string, agencyId: string) {
  return createPatientsApi({ hideMessage: true })
    .then((api) => api.post<BaseResponse<ReferralSource>>(`/patient/${patientId}/referral-source`, { agencyId }))
    .then((response) => {
      const referralSource = response.data?.data;
      if (response.ok && referralSource) {
        return referralSource;
      }

      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function updateReferralSource(patientId: string, agencyId: string) {
  return createPatientsApi({ hideMessage: true })
    .then((api) => api.put<BaseResponse<ReferralSource>>(`/patient/${patientId}/referral-source`, { agencyId }))
    .then((response) => {
      const referralSource = response.data?.data;
      if (response.ok && referralSource) {
        return referralSource;
      }

      return Promise.reject(new Error(response.data?.description || 'Something went wrong. Please refresh page to try again.'));
    });
}

export function fetchFiles(patientId: string, context = 'files', page = 0) {
  return createFileManagerApi({ hideMessage: true })
    .then((api) =>
      api.get<any>(`/v1/${patientId}/${context}`, {
        page,
        max_results: 100,
      })
    )
    .then((response) => response.data._items || []);
}
